<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col v-if="editMode" class="text-right">
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip
                v-show="hasChanges"
                :color="missingRequiredFields.length ? 'warning' : 'blue'"
                text="Changes Detected"
                icon="info"
                dark
                small
                v-on="on"
              >
              </base-chip>
            </template>
            <span v-if="missingRequiredFields.length">Missing required fields!</span>
            <span v-else>Click the FAB in the bottom right then click "Save Deal" to keep your changes!</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-toolbar flat dense color="secondary" dark>
              <v-toolbar-title>Details</v-toolbar-title>
            </v-toolbar>
            <v-card-text class="pb-0">
              <v-row>
                <v-col v-if="editMode || isRenewal" cols="12">
                  <v-subheader>Partner</v-subheader>
                  <v-card outlined>
                    <editor-toolbar
                      :is-loading="isLoading"
                      :item="partner.partner.default"
                      :slug="partner.slug"
                      :is-archived="Boolean(partner.archived)"
                      type="deal"
                    />
                  </v-card>
                </v-col>
                <v-col cols="12" class="pb-0">
                  <v-subheader>Setup</v-subheader>
                </v-col>
                <v-col v-if="!(editMode || isRenewal)" sm="4" class="py-0">
                  <select-list
                    v-model="deal.partner"
                    :items="partnerOptions"
                    custom-type="avatar"
                    :readonly="isReadonly"
                    label="Partner"
                    outlined
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <text-input
                    v-model="deal.name"
                    :readonly="isReadonly"
                    label="Deal Name"
                    max="50"
                    counter
                    outlined
                    :required="isRequired('name')"
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <select-list
                    v-model="deal.status"
                    :items="getItemsForOption('dealStatus')"
                    :required="isRequired('status')"
                    :readonly="isReadonly"
                    custom-type="deal-status"
                    label="Status"
                    outlined
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <autocomplete-input
                    v-model="deal.type"
                    :readonly="isReadonly"
                    :items="getItemsForOption('dealTypes')"
                    :required="isRequired('type')"
                    label="Deal Type"
                    outlined
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <select-list
                    v-model="deal.currency"
                    :readonly="isReadonly"
                    :items="getItemsForOption('dealCurrencies')"
                    :required="isRequired('currency')"
                    custom-type="icon"
                    label="Currency"
                    outlined
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <select-list
                    v-model="deal.payment_type"
                    :readonly="isReadonly"
                    :items="getItemsForOption('dealPayment')"
                    :required="isRequired('payment_type')"
                    label="Payment Type"
                    outlined
                  />
                </v-col>
                <v-col cols="12" class="py-0">
                  <select-list
                    v-model="deal.offer_types"
                    :readonly="isReadonly"
                    :items="getItemsForOption('offerTypes')"
                    :required="isRequired('offer_types')"
                    label="Offer Types"
                    :limit="6"
                    multiple
                    outlined
                  />
                </v-col>
                <v-col cols="12" class="py-0">
                  <select-list
                    v-model="deal.countries"
                    :readonly="isReadonly"
                    :items="getItemsForOption('countries')"
                    :limit="6"
                    :required="isRequired('countries')"
                    custom-type="country"
                    label="Countries"
                    multiple
                    outlined
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="pb-0">
                  <v-subheader>Active Date</v-subheader>
                </v-col>
                <v-col sm="4" class="py-0">
                  <date-picker
                    v-model="deal.start"
                    :readonly="isReadonly"
                    :required="isRequired('start')"
                    format="DDMMYYYY"
                    label="Start Date"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="4" class="py-0">
                  <date-picker
                    v-model="deal.end"
                    :readonly="isReadonly"
                    :required="isRequired('end')"
                    format="DDMMYYYY"
                    label="End Date"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-subheader>Misc.</v-subheader>
                  <textarea-input
                    v-model="deal.notes"
                    :readonly="isReadonly"
                    label="Notes"
                    row-height="60"
                    :required="isRequired('notes')"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col>
          <v-card outlined>
            <v-toolbar flat color="secondary" dark>
              <v-toolbar-title class="mr-6">Commission Rates</v-toolbar-title>
              <v-spacer />
              <div class="d-flex align-center">
                <select-list
                  v-model="newCommissionCountry"
                  :disabled="isReadonly"
                  :items="commissionCountriesFilteredByDeal"
                  label="Select Country"
                  outlined
                  hide-details
                />
                <v-btn
                  :disabled="!newCommissionCountry"
                  color="primary"
                  class="ml-2"
                  depressed
                  @click="onClickAddCountry('newCommissionCountry', 'commission_rates')"
                  >Add</v-btn
                >
                <v-btn
                  :disabled="!commissionCountriesFilteredByDeal.length"
                  color="primary"
                  class="ml-2"
                  depressed
                  @click="onClickAddAllCountries(commissionCountriesFilteredByDeal, 'commission_rates')"
                  >Add All</v-btn
                >
              </div>
            </v-toolbar>
            <v-card-text class="py-0">
              <v-virtual-scroll
                v-if="orderedComissionRates.length"
                :items="orderedComissionRates"
                :max-height="commisionRatesExpanded ? 700 : 300"
                item-height="64"
                bench="10"
              >
                <template v-slot:default="{ item }">
                  <div :key="item[0]" :title="item[0]" class="d-flex justify-between align-baseline">
                    <v-col sm="3" :title="formattedOption('countries', item[0])" class="overflow">
                      {{ formattedOption('countries', item[0], 'value', 'emoji') }}
                      {{ formattedOption('countries', item[0]) }}
                    </v-col>
                    <v-col sm="3">
                      <text-input
                        :readonly="isReadonly"
                        :value="deal.commission_rates[item[0]].value"
                        label="Amount"
                        type="number"
                        hide-details
                        outlined
                        @input="onEditField(['commission_rates', item[0], 'value'], $event)"
                      />
                    </v-col>
                    <v-col sm="3">
                      <text-input
                        :readonly="isReadonly"
                        :value="deal.commission_rates[item[0]].qualifier"
                        label="Qualifier"
                        hide-details
                        outlined
                        @input="onEditField(['commission_rates', item[0], 'qualifier'], $event)"
                      />
                    </v-col>
                    <v-col class="d-flex align-center justify-end" sm="3">
                      <v-tooltip bottom small>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="!commissionCountriesFilteredByDeal.length || isReadonly"
                            class="white--text ml-2"
                            color="blue"
                            depressed
                            v-on="on"
                            @click="onCloneGeos('commission_rates', item[1])"
                            ><v-icon small>content_copy</v-icon>
                          </v-btn>
                        </template>
                        <span>Copy to all REMAINING countries</span>
                      </v-tooltip>
                      <v-tooltip bottom small>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="isReadonly"
                            class="white--text ml-2"
                            color="error"
                            depressed
                            title="Delete"
                            @click="onDeleteField('commission_rates', item[0])"
                            v-on="on"
                            ><v-icon small>delete_outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </v-col>
                  </div>
                </template>
              </v-virtual-scroll>
              <div v-else class="text-caption py-6">
                Add a country to update commision rates.
              </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-space-between">
              <toggle-input
                v-model="commisionRatesExpanded"
                label="Expand"
                :is-disabled="!(deal.commission_rates && Object.keys(deal.commission_rates).length > 5)"
              />
              <v-btn
                class="white--text"
                color="error"
                small
                depressed
                :disabled="!(deal.commission_rates && Object.keys(deal.commission_rates).length) || isReadonly"
                @click="onDeleteAllFields('commission_rates')"
              >
                <span>Remove all</span>
                <v-icon small right>delete_outline</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col>
          <v-card outlined>
            <v-toolbar flat color="secondary" dark>
              <v-toolbar-title>Conversion Rate Guarantee</v-toolbar-title>
              <v-spacer />
              <select-list
                v-model="newCRGCountry"
                :disabled="isReadonly"
                :items="crgCountriesFilteredByDeal"
                label="Select Country"
                outlined
                hide-details
              />
              <v-btn
                class="ml-2"
                color="primary"
                :disabled="!newCRGCountry"
                depressed
                @click="onClickAddCountry('newCRGCountry', 'conversion_rate_guarantee')"
                >Add</v-btn
              >
              <v-btn
                :disabled="!crgCountriesFilteredByDeal.length"
                color="primary"
                class="ml-2"
                depressed
                @click="onClickAddAllCountries(crgCountriesFilteredByDeal, 'conversion_rate_guarantee')"
                >Add All</v-btn
              >
            </v-toolbar>
            <v-card-text class="py-0">
              <v-virtual-scroll
                v-if="orderedConversionRates.length"
                :items="orderedConversionRates"
                :max-height="conversionRatesExpanded ? 700 : 300"
                item-height="64"
                bench="10"
              >
                <template v-slot:default="{ item }">
                  <div :key="item[0]" :title="item[0]" class="d-flex justify-between align-baseline">
                    <v-col sm="3" :title="formattedOption('countries', item[0])" class="overflow">
                      {{ formattedOption('countries', item[0], 'value', 'emoji') }}
                      {{ formattedOption('countries', item[0]) }}
                    </v-col>
                    <v-col sm="3">
                      <text-input
                        :readonly="isReadonly"
                        :value="deal.conversion_rate_guarantee[item[0]].percentage"
                        label="Percentage"
                        type="number"
                        outlined
                        hide-details
                        @input="onEditField(['conversion_rate_guarantee', item[0], 'percentage'], $event)"
                      />
                    </v-col>
                    <v-col sm="3">
                      <text-input
                        :readonly="isReadonly"
                        :value="deal.conversion_rate_guarantee[item[0]].min_lead_volume"
                        label="Miniumum Lead Volume"
                        type="number"
                        outlined
                        hide-details
                        @input="onEditField(['conversion_rate_guarantee', item[0], 'min_lead_volume'], $event)"
                      />
                    </v-col>
                    <v-col class="d-flex align-center justify-end" sm="3">
                      <v-tooltip bottom small>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="!crgCountriesFilteredByDeal.length || isReadonly"
                            class="white--text ml-2"
                            color="blue"
                            depressed
                            v-on="on"
                            @click="onCloneGeos('conversion_rate_guarantee', item[1])"
                            ><v-icon small>content_copy</v-icon>
                          </v-btn>
                        </template>
                        <span>Copy to all REMAINING countries</span>
                      </v-tooltip>
                      <v-tooltip bottom small>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="isReadonly"
                            class="white--text ml-2"
                            color="error"
                            depressed
                            title="Delete"
                            @click="onDeleteField('conversion_rate_guarantee', item[0])"
                            v-on="on"
                            ><v-icon small>delete_outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </v-col>
                  </div>
                </template>
              </v-virtual-scroll>
              <div v-else class="text-caption py-6">
                Add a country to update conversion rate guarentees.
              </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-space-between">
              <toggle-input
                v-model="conversionRatesExpanded"
                label="Expand"
                :is-disabled="
                  !(deal.conversion_rate_guarantee && Object.keys(deal.conversion_rate_guarantee).length > 5)
                "
              />
              <v-btn
                :disabled="
                  !(deal.conversion_rate_guarantee && Object.keys(deal.conversion_rate_guarantee).length) || isReadonly
                "
                class="white--text"
                color="error"
                depressed
                small
                @click="onDeleteAllFields('conversion_rate_guarantee')"
              >
                <span>Remove all</span>
                <v-icon small right>delete_outline</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-delete="editMode && isPermittedTo('delete')"
      :can-edit="(!editMode && isPermittedTo('create')) || (editMode && isPermittedTo('update'))"
      :can-renew="editMode && isPermittedTo('update')"
      :has-changes="hasChanges"
      :always-save="!editMode"
      context="Deal"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @delete="showDeleteModal = true"
      @renew="showRenewModal = true"
    />

    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="deal"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Deal"
      persistent
      :is-confirm-disabled="!!missingRequiredFields.length"
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <div v-if="missingRequiredFields.length">
        <span class="font-weight-bold error--text">Missing required fields:</span>
        <ul>
          <li v-for="field in missingRequiredFields" :key="field">
            {{ startCase(field) }}
          </li>
        </ul>
      </div>
      <span v-else>Are you sure you want to save these changes?</span>
    </generic-modal>

    <!-- Renew Modal -->
    <generic-modal
      v-model="showRenewModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Renew Deal"
      hide-louis
      persistent
      @modal-confirm="onRenewConfirm"
    >
      <div>
        Are you sure you want to renew this deal (<span class="font-weight-bold">{{ deal.name }}</span
        >)?
      </div>
      <div class="mt-4">
        Confirming will end this deal and redirect to the 'Create New Deal' page, passing this deal's data as a
        foundation.
      </div>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Deal"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this deal? This cannot be
        undone.
      </div>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations, mapState } from 'vuex'
import { cloneDeep, merge, startCase } from 'lodash-es'
import { buildObjectFromArray } from '@/utils/objects'
import { formatDate } from '@/utils/date'
import isSet from '@/utils/is-set'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import BaseChip from '@/components/chips/base-chip'
import DatePicker from '@/components/forms/date-picker'
import EditorToolbar from '@/components/editor-toolbar'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import SelectList from '@/components/forms/select-list'
import TextareaInput from '@/components/forms/textarea-input'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
// Services
import { deals, partners, partnerReports } from '@/services/offers'
const transform = ({ start, end, ...rest }) => ({
  start: formatDate(start, { separator: '-' }) || null,
  end: formatDate(end, { separator: '-' }) || null,
  ...rest,
})
const dealModel = ({
  commission_rates = {},
  conversion_rate_guarantee = {},
  countries = [],
  currency = null,
  end = null,
  flat_fee = [],
  offer_types = [],
  name = '',
  notes = null,
  partner = null,
  payment_type = null,
  start = null,
  status = null,
  tags = [],
  type = null,
} = {}) => ({
  commission_rates,
  conversion_rate_guarantee,
  countries,
  currency,
  end,
  flat_fee,
  offer_types,
  name,
  notes,
  partner,
  payment_type,
  start,
  status,
  tags,
  type,
})
export default {
  name: 'DealsEditor',
  components: {
    AutocompleteInput,
    BaseChip,
    DatePicker,
    EditorToolbar,
    ExitModal,
    FabActions,
    GenericModal,
    PageLoader,
    ResetModal,
    SelectList,
    TextareaInput,
    TextInput,
    ToggleInput,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    commisionRatesExpanded: false,
    conversionRatesExpanded: false,
    deal: dealModel(),
    isNewDealCreated: false,
    isLoading: false,
    mode: 'create',
    newCommissionCountry: null,
    newCRGCountry: null,
    original: dealModel(),
    partner: {
      partner: { default: {} },
    },
    partnerOptions: [],
    showDeleteModal: false,
    showRenewModal: false,
    showResetModal: false,
    showSaveModal: false,
  }),
  computed: {
    ...mapState({ schema: state => state.deals.schema }),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    breadcrumbs() {
      return [
        { text: 'Deal Management', to: { name: 'deals.management' }, exact: true },
        {
          text: (!this.editMode && 'New Deal') || this.original.name || 'No Deal Name',
          disabled: true,
        },
      ]
    },
    commissionCountriesFilteredByDeal() {
      if (!Object.keys(this.deal).length) return []
      return this.getItemsForOption('countries').filter(
        ({ value }) => this.deal.countries.includes(value) && !Object.keys(this.deal.commission_rates).includes(value)
      )
    },
    crgCountriesFilteredByDeal() {
      if (!Object.keys(this.deal).length) return []
      return this.getItemsForOption('countries').filter(
        ({ value }) =>
          this.deal.countries.includes(value) && !Object.keys(this.deal.conversion_rate_guarantee).includes(value)
      )
    },
    editMode() {
      return this.mode === 'edit'
    },
    hasChanges() {
      return this.hasDataChanged(this.deal, this.original)
    },
    isReadonly() {
      return (
        (!this.isPermittedTo('create') && !this.editMode) ||
        (!this.isPermittedTo('update') && this.editMode) ||
        this.isLoading
      )
    },
    isRenewal() {
      return !!this.$route.params.renewal
    },
    missingRequiredFields() {
      return this.schema?.required.filter(field => !isSet(this.deal[field])) || []
    },
    orderedComissionRates() {
      return this.orderCountriesIsoByName(this.deal.commission_rates)
    },
    orderedConversionRates() {
      return this.orderCountriesIsoByName(this.deal.conversion_rate_guarantee)
    },
  },
  watch: {
    'deal.countries'(updated, previous) {
      const removedCountries = previous.filter(country => !updated.includes(country))
      removedCountries.forEach(country => {
        this.onDeleteField('commission_rates', country)
        this.onDeleteField('conversion_rate_guarantee', country)
      })
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.mode = 'edit'
      await this.getDeal()
      await this.getPartner()
    } else if (this.$route.params.renewal) {
      this.deal = dealModel(this.$route.params.renewal)
      this.original = dealModel(this.$route.params.renewal)
      await this.getPartner()
    } else {
      await this.getAllPartners()
    }
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if ((!this.hasChanges && !this.isRenewal) || (!this.hasChanges && this.isRenewal && this.isNewDealCreated))
      return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    startCase,
    async getDeal() {
      this.isLoading = true
      try {
        this.deal = await deals.getDeal(this.$route.params.id)
        this.original = cloneDeep(this.deal)
      } catch (error) {
        // Todo: Add 404 redirect
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getPartner() {
      this.isLoading = true
      try {
        this.partner = await partners.getSinglePartner(this.deal.partner)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partner',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getAllPartners() {
      this.isLoading = true
      try {
        const { data } = await partners.getAllPartners({ compact: true })
        this.partnerOptions = data.map(({ name, slug, logoThumbnail }) => ({
          key: name,
          value: slug,
          icon: logoThumbnail,
        }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partner options',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    isRequired(field) {
      if (!this.schema?.required) return false
      return this.schema.required.includes(field)
    },
    onBeforeUnload(event) {
      if ((!this.hasChanges && !this.isRenewal) || (!this.hasChanges && this.isRenewal && this.isNewDealCreated)) return
      this.confirmUnload(event)
    },
    onClickAddCountry(countryKey, key) {
      const country = this[countryKey]
      this.onEditField([key, country], {})
      this[countryKey] = null
    },
    onClickAddAllCountries(countries, key) {
      countries.forEach(({ value: country }) => {
        this.onEditField([key, country], {})
      })
    },
    onCloneGeos(key, data) {
      const built = this.deal.countries
        .filter(code => !Object.keys(this.deal[key]).includes(code))
        .reduce((acc, code) => {
          acc[code] = Object.assign({}, data)
          return acc
        }, {})
      this.deal = merge({}, this.deal, { [key]: built })
    },
    onDeleteField(key, country) {
      if (!this.deal[key][country]) return
      const deal = cloneDeep(this.deal)
      delete deal[key][country]
      this.deal = deal
    },
    onDeleteAllFields(key) {
      const deal = cloneDeep(this.deal)
      const keyType = typeof deal[key]
      switch (keyType) {
        case 'object':
          if (Array.isArray(keyType)) {
            deal[key] = []
          } else {
            deal[key] = {}
          }
          break
        default:
          deal[key] = null
          break
      }
      this.deal = deal
    },
    async onDeleteConfirm() {
      this.isLoading = true
      try {
        await deals.deleteDeal(this.deal)
        this.$notify({
          type: 'success',
          text: `Successfully removed deal '${this.original.name}'`,
        })
        this.deal = cloneDeep(this.original)
        this.$router.push({ name: 'deals.management' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onEditField(keys, value = null) {
      const data = buildObjectFromArray(keys, value)
      this.deal = Array.isArray(value) ? assign({}, this.deal, data) : merge({}, this.deal, data)
    },
    async onRenewConfirm() {
      this.isLoading = true
      try {
        const linkedPartnerReports = await this.fetchPartnerReportsByDeal()
        const endedDeal = dealModel(
          merge({}, this.deal, { end: formatDate(new Date(), { separator: '-' }), status: 'expired' })
        )
        const newDeal = dealModel(merge({}, this.deal, { end: null, start: null, status: null }))
        await deals.updateDeal(this.deal.id, transform(endedDeal))
        this.$notify({
          type: 'success',
          text: 'Successfully ended deal',
        })
        this.deal = cloneDeep(endedDeal)
        this.original = cloneDeep(endedDeal)
        this.$router.push({ name: 'deals.create', params: { renewal: newDeal, linkedPartnerReports } })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to renew deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onResetConfirm() {
      this.deal = cloneDeep(this.original)
      this.showResetModal = false
    },
    async onSaveConfirm() {
      this.isLoading = true
      const request = this.editMode
        ? deals.updateDeal(this.deal.id, transform(this.deal))
        : deals.createDeal(transform(this.deal))
      try {
        const { id } = await request
        if (this.editMode) {
          this.showSaveModal = false
          this.$notify({
            type: 'success',
            text: 'Successfully saved deal',
          })
          this.original = cloneDeep(this.deal)
        } else {
          this.$notify({
            type: 'success',
            text: 'Successfully created deal',
          })
          await this.relinkPartnerReportsToNewDeal(id)
          this.original = cloneDeep(this.deal)
          this.isNewDealCreated = true
          this.$router.replace({ name: 'deals.edit', params: { id } })
        }
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Unable to save deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async relinkPartnerReportsToNewDeal(dealID) {
      const reports = this.$route.params.linkedPartnerReports
      if (!reports || !reports.length) return null

      try {
        const response = await Promise.all(
          reports.map(report => partnerReports.updateReport(report.id, { ...report, deal_id: dealID }))
        )
        this.$notify({
          type: 'success',
          text: 'Successfully linked partner reports',
        })
        return response
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to link partner reports to deal',
          data: { error },
          duration: -1,
        })
        return null
      }
    },
    async fetchPartnerReportsByDeal() {
      try {
        const { data } = await partnerReports.getFilteredReports({
          filters: { deal_id: this.deal.id },
        })
        return data || []
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch linked partner reports',
          data: { error },
          duration: -1,
        })
        return []
      }
    },
    orderCountriesIsoByName(dealProperty) {
      return Object.entries(dealProperty).sort(([a], [b]) => {
        const countryA = this.formattedOption('countries', a)
        const countryB = this.formattedOption('countries', b)
        return countryA == countryB ? 0 : countryA > countryB ? 1 : -1
      })
    },
  },
}
</script>
